import { rotationFromDirection, View } from "@novorender/api";
import { glMatrix, quat, ReadonlyVec3, ReadonlyVec4, vec2, vec3 } from "gl-matrix";

import { getCameraDir } from "features/engine2D/utils";
import { CameraState, CameraType } from "features/render";
import { pointToPlaneDistance } from "utils/math";

export async function getTopDownParams({
    view,
    elevation,
    snapToNearestAxis,
    far,
}: {
    view: View;
    elevation: number | undefined;
    snapToNearestAxis?: boolean;
    far: number;
}): Promise<{
    position: vec3;
    rotation: quat;
    fov: number;
    far?: number;
}> {
    const { width, height } = view.canvas.getBoundingClientRect();
    const pick = await view.pick(Math.round(width / 2), Math.round(height / 2), {
        sampleDiscRadius: 200,
        async: true,
        searchType: "closestToPickCenter",
    });

    let position = vec3.clone(view.renderState.camera.position);
    let z = elevation ?? position[2];
    let fov: number;
    if (pick) {
        position = pick.position;
        // Idea behind the formula (based on top-down 3d->2d transition, but we apply it in any case):
        // There's a right triangle formed by camera, pick position in the center of the screen, and the top point of the screen.
        // Triangle is not exactly right because pick position might be slightly off the center.
        //      C
        //     /|
        //    / |
        //   /  |
        //  /   |
        // T____P
        // where C - camera, T - top of the screen, P - pick position
        // We know distance C_P and angle CT_CP (half pinhole FOV), so we can find T_P distance using tan(CT_CP)
        // Then T_P multiplied by 2 gives us screen height, which is ortho FOV.
        fov =
            Math.tan(glMatrix.toRadian(view.renderState.camera.fov / 2)) *
            vec3.dist(view.renderState.camera.position, position) *
            2;
        z = position[2] + vec3.dist(view.renderState.camera.position, position);
    } else {
        fov = z;
    }

    // Adjust Z and far to the scene's bounding box height to see the entire scene
    // Assuming there's no depth lower than -10000 and no height higher than 10000
    const zRange = vec2.fromValues(-10000, 10000);
    // NOTE: scene bbox is not reliable as of 2025-10-17 (can contain min/max floats)
    const bbox = view.renderState.scene?.config.aabb;
    if (bbox) {
        zRange[0] = Math.max(zRange[0], bbox.min[2]);
        zRange[1] = Math.min(zRange[1], bbox.max[2]);
    }
    const bs = view.renderState.scene?.config.boundingSphere;
    if (bs) {
        zRange[0] = Math.max(zRange[0], bs.center[2] - bs.radius);
        zRange[1] = Math.min(zRange[1], bs.center[2] + bs.radius);
    }

    const offset = 0.5; // offset around Z range
    position[2] = zRange[1] + offset;
    const minFar = Math.max(1000, far);
    const maxFar = 500_000;
    const newFar = Math.min(maxFar, Math.ceil(zRange[1] - zRange[0]) + offset * 2);

    return {
        position,
        rotation: getTopDownRotation(view, snapToNearestAxis),
        fov: Math.max(fov, 20),
        ...(newFar > minFar ? { far: newFar } : {}),
    };
}

export function getTopDownRotation(view: View, snapToNearestAxis = false) {
    return rotationFromDirection([0, 0, 1], snapToNearestAxis ? view.renderState.camera.rotation : undefined);
}

export function getSnapToPlaneParams({
    planeIdx,
    view,
    anchorPos,
    offset,
    inferOrthoFov,
}: {
    planeIdx: number;
    view: View;
    anchorPos?: ReadonlyVec3;
    offset?: number;
    /**
     * Set FOV based on the original distance from camera to the clipping plane (relevant for ortho camera)
     * to preserve visual distance from the plane.
     */
    inferOrthoFov?: boolean;
}): {
    position: vec3;
    rotation: quat;
    fov: number;
    far: number;
} {
    const p = view.renderState.clipping.planes[planeIdx].normalOffset;
    const dir = vec3.fromValues(p[0], p[1], p[2]);
    const rotation = rotationFromDirection(dir);
    let position: ReadonlyVec3;
    if (anchorPos) {
        position = vec3.clone(anchorPos);
        if (offset) {
            vec3.scaleAndAdd(position, position, dir, offset);
        }
    } else {
        const planePoint = vec3.scaleAndAdd(vec3.create(), vec3.create(), dir, p[3]);
        const v = vec3.sub(vec3.create(), view.renderState.camera.position, planePoint);
        const d = vec3.dot(v, dir);
        position = vec3.scaleAndAdd(vec3.create(), view.renderState.camera.position, dir, -d);
    }

    const fov = inferOrthoFov ? pointToPlaneDistance(view.renderState.camera.position, p) : view.renderState.camera.fov;

    return { position, rotation, fov, far: 0.001 };
}

export function isCrossSectionAndAlignedWithPlane(camera: CameraState, normalOffset: ReadonlyVec4) {
    if (camera.type !== CameraType.Orthographic) {
        return false;
    }
    const rotation = camera.goTo?.rotation;
    if (!rotation) {
        return false;
    }
    const far = camera.goTo?.far;
    if (far === undefined || Math.abs(far) > 1) {
        // If far is too big - it's not a cross section, just a regular ortho view
        // and our small (0 to 1) far clipping plane slider might be confusing.
        return false;
    }
    const cameraDir = getCameraDir(rotation);
    const planeNormal = vec3.fromValues(normalOffset[0], normalOffset[1], normalOffset[2]);
    return Math.abs(vec3.dot(planeNormal, cameraDir)) >= 0.99;
}

export async function getBestAxisAlignedView(
    view: View,
    camera?: {
        position: vec3;
        rotation: quat;
        fov: number;
    },
): Promise<{
    position: vec3;
    rotation: quat;
    fov: number;
}> {
    const cam = camera ?? view.renderState.camera;
    // Get current camera forward direction
    const forward = vec3.create();
    vec3.transformQuat(forward, vec3.fromValues(0, 0, -1), cam.rotation);

    // Define standard orthographic view directions
    const axisAlignedViews = [
        {
            name: "bottom",
            direction: vec3.fromValues(0, 0, -1),
            rotation: rotationFromDirection(vec3.fromValues(0, 0, -1)),
        },
        {
            name: "top",
            direction: vec3.fromValues(0, 0, 1),
            rotation: rotationFromDirection(vec3.fromValues(0, 0, 1)),
        },
        {
            name: "back",
            direction: vec3.fromValues(0, 1, 0),
            rotation: rotationFromDirection(vec3.fromValues(0, 1, 0)),
        },
        {
            name: "front",
            direction: vec3.fromValues(0, -1, 0),
            rotation: rotationFromDirection(vec3.fromValues(0, -1, 0)),
        },
        {
            name: "left",
            direction: vec3.fromValues(1, 0, 0),
            rotation: rotationFromDirection(vec3.fromValues(1, 0, 0)),
        },
        {
            name: "right",
            direction: vec3.fromValues(-1, 0, 0),
            rotation: rotationFromDirection(vec3.fromValues(-1, 0, 0)),
        },
    ];

    // Find the orthographic view that shows what we're currently looking at
    // Select the view with the most opposite direction (lowest dot product)
    let bestMatch = axisAlignedViews[0];
    let lowestDotProduct = vec3.dot(forward, bestMatch.direction);

    for (const axisView of axisAlignedViews) {
        const dotProduct = vec3.dot(forward, axisView.direction);
        if (dotProduct < lowestDotProduct) {
            lowestDotProduct = dotProduct;
            bestMatch = axisView;
        }
    }
    const { width, height } = view.canvas.getBoundingClientRect();
    const pick = await view.pick(Math.round(width / 2), Math.round(height / 2), {
        sampleDiscRadius: 200,
        async: true,
        searchType: "closestToPickCenter",
    });

    if (pick) {
        // If we have a pick, we want to center the orthographic view on it.
        // We can calculate the camera position by moving from the pick position in the opposite direction of the view,
        // at a distance equal to the current camera distance to the pick point.
        const cameraToPick = vec3.distance(cam.position, pick.position);
        const position = vec3.scaleAndAdd(vec3.create(), pick.position, bestMatch.direction, cameraToPick);
        return {
            position,
            rotation: bestMatch.rotation,
            fov: cameraToPick,
        };
    }

    const sceneBoundingSphere = view.renderState.scene?.config.boundingSphere;

    // Calculate camera position for the selected orthographic view
    if (sceneBoundingSphere) {
        // calculate relative position to the center of the bounding sphere
        const cameraRadius = vec3.distance(cam.position, sceneBoundingSphere.center);
        const inside = cameraRadius < sceneBoundingSphere.radius;

        const cameraDirection = vec3.create();
        vec3.transformQuat(cameraDirection, vec3.fromValues(0, 0, -1), bestMatch.rotation);

        const position = vec3.scaleAndAdd(vec3.create(), sceneBoundingSphere.center, cameraDirection, -cameraRadius);

        const fovmul = inside ? 0.25 : 0.5;
        // clamp ortho FOV (frustum height in this case) to avoid extreme values when the camera is very close or very far from the scene
        const fov = Math.min(Math.max(cameraRadius * fovmul, 2.0), 250.0);

        return {
            position: inside ? cam.position : position,
            rotation: bestMatch.rotation,
            fov,
        };
    } else {
        // Fallback to current position if no bounding sphere available
        return {
            position: cam.position,
            rotation: bestMatch.rotation,
            fov: 5,
        };
    }
}
